import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import classes from './DealerInquiryForm.module.scss';
import AuthContext from '../../store/auth-context';
import { createDealerInquiry, snapshotFromCar } from '../../services/dealerGarage';
import { buildVehicleTitle } from '../../helper/vehicleTitle';
import { formatUsd, US_PORTS } from '../../services/dealerStock';

const COPY = {
    inquiry: {
        title: 'Inquire About This Vehicle',
        intro: (name) => `Ask us anything about this ${name} - availability, condition, or the import timeline. We reply by email within one business day.`,
        placeholder: 'What would you like to know?',
        submit: 'Send Inquiry',
    },
    quote: {
        title: 'Request This Quote',
        intro: (name) => `We will confirm the figures for this ${name} and come back with a firm, all-in quote.`,
        placeholder: 'Anything we should know? Destination address, timing, financing...',
        submit: 'Request Quote',
    },
    inspection: {
        title: 'Request an Inspection',
        intro: (name) => `Our team in Japan can inspect this ${name} in person before you commit. Tell us what to look at.`,
        placeholder: 'Any specific areas you would like inspected (underbody, engine bay, paint, modifications...)',
        submit: 'Request Inspection',
    },
};

/**
 * Inline inquiry / quote / inspection form for a dealer-stock car.
 *
 * Posts to the auction API (POST /dealer/inquiries) rather than linking out to
 * the marketing site's contact page: the request is then recorded against the
 * member's account, shows up in their garage on both sites, and lands in the
 * team's inbox through the API's own mailer.
 */
const DealerInquiryForm = ({ vehicle, type = 'inquiry', quote = null, onClose }) => {
    const authCtx = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const user = authCtx.userData || {};
    const copy = COPY[type] || COPY.inquiry;
    const name = vehicle ? buildVehicleTitle(vehicle) : 'vehicle';

    const [form, setForm] = useState({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        message: '',
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const firstFieldRef = useRef(null);

    // Fresh form for a different car or a different kind of request.
    useEffect(() => {
        setSent(false);
        setError('');
        setForm((prev) => ({ ...prev, message: '' }));
        if (firstFieldRef.current) firstFieldRef.current.focus();
    }, [vehicle && vehicle._id, type]);

    const onChange = (event) => {
        const { name: field, value } = event.target;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (sending) return;
        if (!form.email.trim()) {
            setError('Please enter your email address so we can reply.');
            return;
        }
        setSending(true);
        setError('');

        const payload = {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            vehicleId: vehicle._id,
            vehicle: snapshotFromCar(vehicle),
            type,
            message: form.message.trim(),
            origin: 'portal',
            page: window.location.href.slice(0, 512),
            notifyTeam: true,
        };
        if (type === 'quote' && quote) {
            payload.quote = {
                quoteType: quote.quoteType || 'shipped',
                port: quote.port || '',
                includeTitle: !!quote.includeTitle,
                estimatedTotal: quote.estimatedTotal || null,
            };
        }

        try {
            const res = await createDealerInquiry(payload);
            if (res?.message === 'Inquiry recorded') {
                setSent(true);
                enqueueSnackbar('Sent - we will be in touch shortly.', { variant: 'success' });
            } else {
                setError(res?.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            setError('Could not send your request. Please check your connection and try again.');
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <section className={classes.form} aria-live="polite">
                <div className={classes.sentState}>
                    <i className="fas fa-check-circle" aria-hidden="true"></i>
                    <h2>Request sent</h2>
                    <p>
                        Thanks{form.firstName ? `, ${form.firstName}` : ''}. We have your {type === 'quote' ? 'quote request' : type === 'inspection' ? 'inspection request' : 'inquiry'} for the {name} and will reply to <strong>{form.email}</strong>. Look for an email from Magic Motorsports.
                    </p>
                    <div className={classes.sentActions}>
                        <Link to="/garage?tab=inquiries" className={classes.primary}>View in My Garage</Link>
                        {onClose && (
                            <button type="button" className={classes.secondary} onClick={onClose}>Close</button>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={classes.form}>
            <div className={classes.header}>
                <div>
                    <h2>{copy.title}</h2>
                    <p>{copy.intro(name)}</p>
                </div>
                {onClose && (
                    <button type="button" className={classes.close} onClick={onClose} aria-label="Close form">
                        <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                )}
            </div>

            {type === 'quote' && quote && (
                <dl className={classes.quoteSummary}>
                    <div>
                        <dt>Quote type</dt>
                        <dd>{quote.quoteType === 'imported' ? 'Imported (customs cleared)' : 'Shipped to US port'}</dd>
                    </div>
                    <div>
                        <dt>Port</dt>
                        <dd>{quote.port && US_PORTS.includes(quote.port) ? quote.port : 'Not chosen yet'}</dd>
                    </div>
                    <div>
                        <dt>Virginia title</dt>
                        <dd>{quote.includeTitle ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                        <dt>Estimate shown</dt>
                        <dd>{quote.estimatedTotal ? formatUsd(quote.estimatedTotal) : 'ASK'}</dd>
                    </div>
                </dl>
            )}

            <form onSubmit={onSubmit} noValidate>
                <div className={classes.row}>
                    <label className={classes.field}>
                        <span>First name</span>
                        <input ref={firstFieldRef} name="firstName" value={form.firstName} onChange={onChange} autoComplete="given-name" />
                    </label>
                    <label className={classes.field}>
                        <span>Last name</span>
                        <input name="lastName" value={form.lastName} onChange={onChange} autoComplete="family-name" />
                    </label>
                </div>
                <div className={classes.row}>
                    <label className={classes.field}>
                        <span>Email <em>*</em></span>
                        <input name="email" type="email" value={form.email} onChange={onChange} autoComplete="email" required />
                    </label>
                    <label className={classes.field}>
                        <span>Phone</span>
                        <input name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" />
                    </label>
                </div>
                <label className={`${classes.field} ${classes.fieldFull}`}>
                    <span>Message</span>
                    <textarea name="message" rows={4} value={form.message} onChange={onChange} placeholder={copy.placeholder} maxLength={4000} />
                </label>

                {error && <p className={classes.error} role="alert">{error}</p>}

                <div className={classes.actions}>
                    <button type="submit" className={classes.primary} disabled={sending}>
                        {sending ? 'Sending...' : copy.submit}
                    </button>
                    {onClose && (
                        <button type="button" className={classes.secondary} onClick={onClose} disabled={sending}>
                            Cancel
                        </button>
                    )}
                </div>
                <p className={classes.note}>
                    Sent from your account, so it is saved under My Garage and you can follow up any time.
                </p>
            </form>
        </section>
    );
};

export default DealerInquiryForm;
